import { api, FormatError, parseList } from '@/lib/api'

import { parseNotification, type Notification } from './types'
import type { PushPreferences, Schemas } from './types'

/**
 * Serializable browser PushSubscription. Keys stay in memory only: they are
 * sent to the API and never rendered or logged.
 */
export interface WebPushSubscriptionPayload {
  endpoint: string
  expirationTime?: number | null
  keys: {
    p256dh: string
    auth: string
  }
}

function parseNotificationRecord(
  value: Record<string, unknown>,
): Notification {
  const notification = parseNotification(value)
  if (!notification) throw new FormatError('Malformed notification record')
  return notification
}

/** Thin wrapper over the frozen `/notifications` contract (API_CONTRACT §7). */
export const notificationsApi = {
  list(signal?: AbortSignal): Promise<Schemas['NotificationList']> {
    return api
      .get<Schemas['NotificationList']>('/notifications', { signal })
      .then((response) => ({
        ...response,
        items: parseList(
          response.items,
          '/notifications',
          parseNotificationRecord,
        ),
      }))
  },

  unreadCount(signal?: AbortSignal): Promise<Schemas['UnreadCountResponse']> {
    return api.get<Schemas['UnreadCountResponse']>(
      '/notifications/unread-count',
      { signal },
    )
  },

  markRead(id: string): Promise<Schemas['NotificationReadResponse']> {
    return api.patch<Schemas['NotificationReadResponse']>(
      `/notifications/${encodeURIComponent(id)}/read`,
    )
  },

  markAllRead(): Promise<Schemas['OkResponse']> {
    return api.post<Schemas['OkResponse']>('/notifications/read-all')
  },

  pushPublicKey(signal?: AbortSignal): Promise<Schemas['PushPublicKeyResponse']> {
    return api.get<Schemas['PushPublicKeyResponse']>(
      '/notifications/push-public-key',
      { signal, skipAuth: true, skipRefresh: true },
    )
  },

  savePushSubscription(
    subscription: WebPushSubscriptionPayload,
    deviceId: string,
  ): Promise<Schemas['OkResponse']> {
    return api.post<Schemas['OkResponse']>('/notifications/push-subscriptions', {
      subscription,
      deviceId,
    })
  },

  removePushSubscription(endpoint: string): Promise<Schemas['OkResponse']> {
    return api.post<Schemas['OkResponse']>(
      '/notifications/push-subscriptions/remove',
      { endpoint },
    )
  },

  registerPushDevice(
    body: Schemas['PushDeviceRequest'],
  ): Promise<Schemas['OkResponse']> {
    return api.post<Schemas['OkResponse']>(
      '/notifications/push-devices',
      body,
    )
  },

  unregisterPushDevice(
    body: Schemas['PushDeviceRemoveRequest'],
  ): Promise<Schemas['OkResponse']> {
    return api.request<Schemas['OkResponse']>('/notifications/push-devices', {
      method: 'DELETE',
      body,
    })
  },

  getPushPreferences(
    signal?: AbortSignal,
  ): Promise<Schemas['PushPreferencesResponse']> {
    return api.get<Schemas['PushPreferencesResponse']>(
      '/notifications/push-preferences',
      { signal },
    )
  },

  updatePushPreferences(
    preferences: Partial<PushPreferences>,
  ): Promise<Schemas['PushPreferencesResponse']> {
    return api.patch<Schemas['PushPreferencesResponse']>(
      '/notifications/push-preferences',
      { preferences },
    )
  },

  getProximitySettings(
    signal?: AbortSignal,
  ): Promise<Schemas['ProximitySettingsResponse']> {
    return api.get<Schemas['ProximitySettingsResponse']>(
      '/notifications/proximity-settings',
      // Background best-effort calls must never drive the refresh/logout
      // machinery; the next trigger retries.
      { signal, skipRefresh: true },
    )
  },

  updateProximitySettings(update: {
    enabled?: boolean
    radiusKm?: number
    debounceMinutes?: number
    selectedCategories?: string[] | null
  }): Promise<Schemas['ProximitySettingsResponse']> {
    return api.patch<Schemas['ProximitySettingsResponse']>(
      '/notifications/proximity-settings',
      { settings: update },
    )
  },

  /**
   * Reports the approximately rounded device position (about 100 m grid,
   * foreground-only capture). A 204 response means stored, or skipped while
   * the feature is disabled; nothing is logged server-side.
   */
  recordLastPosition(coordinates: {
    latitude: number
    longitude: number
  }): Promise<void> {
    return api.post<void>('/notifications/last-position', coordinates, {
      skipRefresh: true,
    })
  },
}
